import { Table, Form, Button, Alert } from 'react-bootstrap';
import { useGetExpensesQuery } from '../../slices/expensesApiSlice';
import { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const ExpenseInvoices = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [categoryInput, setCategoryInput] = useState('');
  const [matchedCategories, setMatchedCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [notesSummary, setNoteSummary] = useState({});
  const [error, setError] = useState('');
  const [showTable, setShowTable] = useState(false);

  const { data: expensesData } = useGetExpensesQuery({});

  useEffect(() => {
    if (expensesData?.expenses) {
      summarizeNotes(expensesData.expenses);
    }
  }, [expensesData]);

  useEffect(() => {
    if (categoryInput && expensesData?.expenses) {
      const uniqueCategories = [...new Set(expensesData.expenses.map(e => e.category).filter(Boolean))];
      const matches = uniqueCategories.filter(cat =>
        cat.toLowerCase().includes(categoryInput.toLowerCase())
      );
      setMatchedCategories(matches);
    } else {
      setMatchedCategories([]);
    }
  }, [categoryInput, expensesData]);

  const summarizeNotes = (expenses) => {
    const summary = {};

    expenses.forEach(expense => {
      const notes = expense.notes?.trim();
      const category = expense.category;
      const amount = Number(expense.amount || 0);
      const date = expense.date;

      const noteKey = notes || (category ? '' : null);
      if (noteKey !== null) {
        if (!summary[noteKey]) {
          summary[noteKey] = {
            category,
            total: amount,
            date,
          };
        } else {
          summary[noteKey].total += amount;
        }
      }
    });

    setNoteSummary(summary);
    setShowTable(true);
  };

  const handleFilterByDate = () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates.');
      return;
    }

    if (endDate < startDate) {
      setError('End date must be after start date.');
      return;
    }

    setError('');

    const filtered = expensesData?.expenses?.filter(expense => {
      const expenseDate = new Date(expense.date).toISOString().split('T')[0];
      const matchesDate = expenseDate >= startDate && expenseDate <= endDate;
      const matchesCategory = selectedCategory ? expense.category === selectedCategory : true;
      return matchesDate && matchesCategory;
    }) || [];

    summarizeNotes(filtered);
  };

  const handleExport = () => {
    const rows = Object.entries(notesSummary).map(([notes, entry]) => ({
      Note: notes || '[Blank]',
      Category: entry.category,
      Date: entry.date ? new Date(entry.date).toLocaleDateString('en-US') : '',
      Total: entry.total,
    }));

    const totalSum = rows.reduce((sum, row) => sum + row.Total, 0);
    rows.push({ Note: 'Total', Category: '', Date: '', Total: totalSum });

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Note Summary');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, `Note_Summary_${startDate || 'All'}_to_${endDate || 'All'}${selectedCategory ? `_${selectedCategory}` : ''}.xlsx`);
  };

  const totalSum = Object.values(notesSummary).reduce((sum, entry) => sum + entry.total, 0);

  return (
    <div className="container mt-4">
      <h1 className="text-center">Invoice Summary by Note</h1>

      <Form className="mb-3 d-flex justify-content-center flex-wrap">
        <Form.Group className="mx-2">
          <Form.Label>Start Date:</Form.Label>
          <Form.Control
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
          />
        </Form.Group>
        <Form.Group className="mx-2">
          <Form.Label>End Date:</Form.Label>
          <Form.Control
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
          />
        </Form.Group>
        <Form.Group className="mx-2 position-relative">
          <Form.Label>Category (type to filter):</Form.Label>
          <Form.Control
            type="text"
            value={categoryInput}
            onChange={e => {
              setCategoryInput(e.target.value);
              setSelectedCategory('');
            }}
            placeholder="Search category..."
          />
          {matchedCategories.length > 0 && (
            <div className="position-absolute bg-white border rounded w-100" style={{ zIndex: 10 }}>
              {matchedCategories.map(cat => (
                <div
                  key={cat}
                  className="px-2 py-1 category-option"
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    setSelectedCategory(cat);
                    setCategoryInput(cat);
                    setMatchedCategories([]);
                  }}
                >
                  {cat}
                </div>
              ))}
            </div>
          )}
        </Form.Group>
        <div className="d-flex align-items-end mx-2">
          <Button variant="primary" onClick={handleFilterByDate}>
            Filter by Date
          </Button>
        </div>
      </Form>

      {error && <Alert variant="danger" className="text-center">{error}</Alert>}

      {showTable && Object.keys(notesSummary).length > 0 && (
        <>
          <Table striped bordered hover responsive>
            <thead className="text-center">
              <tr>
                <th>Note</th>
                <th>Category</th>
                <th>Date</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody className="text-center">
              {Object.entries(notesSummary).map(([notes, entry]) => (
                <tr key={notes}>
                  <td>{notes || '[Blank]'}</td>
                  <td>{entry.category || '-'}</td>
                  <td>{entry.date ? new Date(entry.date).toLocaleDateString('en-US') : '-'}</td>
                  <td>{entry.total.toFixed(2)}</td>
                </tr>
              ))}
              <tr className="fw-bold">
                <td>Total</td>
                <td></td>
                <td></td>
                <td>{totalSum.toFixed(2)}</td>
              </tr>
            </tbody>
          </Table>

          <div className="text-center mt-3">
            <Button variant="success" onClick={handleExport}>
              Export to Excel
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default ExpenseInvoices;